/**
 * @fileoverview Molecule: an accessible dialog rendered above the page.
 * @module components/molecules/Modal
 */

import {
	useEffect,
	useId,
	useRef,
	type MouseEvent,
	type ReactNode,
} from "react"
import { createPortal } from "react-dom"

import { Button, Icon } from "@/components/atoms"

import "./Modal.css"

/** Props accepted by {@link Modal}. */
export interface ModalProps {
	/** Heading announced when the dialog opens. */
	readonly title: string
	/** Called on Escape, an overlay click, or the close button. */
	readonly onClose: () => void
	/** Dialog content. */
	readonly children: ReactNode
}

/**
 * Generic dialog: overlay, heading, close control, portalled to `<body>`.
 *
 * @description
 * Portalled so it always sits above the mono-screen dashboard, whose root
 * clips overflow to keep the layout scroll-free. Closes on Escape and on an
 * overlay click; a click inside the panel does not bubble to the overlay.
 *
 * @param props - Title, close handler and content.
 * @returns The rendered dialog.
 *
 * @example
 * <Modal title="Ajouter un achat" onClose={close}>{form}</Modal>
 */
export function Modal({ title, onClose, children }: ModalProps) {
	const titleId = useId()
	const panelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose()
		}

		document.addEventListener("keydown", onKeyDown)
		const { overflow } = document.body.style
		document.body.style.overflow = "hidden"
		panelRef.current?.focus()

		return () => {
			document.removeEventListener("keydown", onKeyDown)
			document.body.style.overflow = overflow
		}
	}, [onClose])

	const stopPropagation = (event: MouseEvent) => {
		event.stopPropagation()
	}

	return createPortal(
		<div className="modal-overlay" onClick={onClose}>
			<div
				ref={panelRef}
				className="modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				tabIndex={-1}
				onClick={stopPropagation}
			>
				<div className="modal__head">
					<h2 className="modal__title" id={titleId}>
						{title}
					</h2>
					<Button
						variant="ghost"
						size="sm"
						iconOnly
						ariaLabel="Fermer"
						onClick={onClose}
					>
						<Icon name="close" size={18} />
					</Button>
				</div>
				<div className="modal__body">{children}</div>
			</div>
		</div>,
		document.body
	)
}
